import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus, Pencil, Wallet } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { formatDate, formatMoney } from '@/lib/format';
import { CampaignDialog } from './CampaignDialog';
import { RecordSpendDialog } from './RecordSpendDialog';
import {
  useCampaignPerformance,
  useCampaignQuality,
  useCampaigns,
  type Campaign,
} from './useCampaigns';

const TABS = ['performance', 'quality', 'manage'] as const;
type Tab = (typeof TABS)[number];

/**
 * Campaign manager and performance dashboard (#17, #18).
 *
 * The quality tab is not a second sort of the same table — it is the comparison
 * itself. #18 asks whether a campaign's lead *quality* matches its volume, and
 * the only way to see that is to put the two rankings side by side and show the
 * gap. A table you have to sort twice and remember does not answer the question.
 */
export default function CampaignsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const { data: me } = useProfile();

  const tabParam = params.get('tab');
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'performance';
  const setTab = (next: Tab) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };

  const canEdit = !!me && ['marketing', 'sales_manager', 'admin'].includes(me.role);

  const performance = useCampaignPerformance();
  const quality = useCampaignQuality();
  const campaigns = useCampaigns();

  const [editing, setEditing] = useState<Campaign | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [spendFor, setSpendFor] = useState<Campaign | undefined>();

  const perf = performance.data ?? [];
  const totals = perf.reduce(
    (acc, r) => {
      acc.spend += Number(r.spend);
      acc.leads += Number(r.leads);
      acc.sales += Number(r.sales);
      acc.commission += Number(r.commission);
      return acc;
    },
    { spend: 0, leads: 0, sales: 0, commission: 0 },
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.campaigns')}</h1>
        {canEdit ? (
          <Button
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            {t('campaigns.create')}
          </Button>
        ) : null}
      </div>

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === name
                ? 'border-brand-700 text-brand-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t(`campaigns.tabs.${name}` as 'campaigns.tabs.performance')}
          </button>
        ))}
      </div>

      {performance.isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      {/* ------------------------------------------------------ performance */}
      {tab === 'performance' ? (
        performance.isPending ? (
          <Spinner />
        ) : perf.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {t('campaigns.none')}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-white p-4 text-center sm:grid-cols-4">
              {(
                [
                  ['spend', formatMoney(totals.spend)],
                  ['leads', String(totals.leads)],
                  ['sales', String(totals.sales)],
                  ['commission', formatMoney(totals.commission)],
                ] as const
              ).map(([key, value]) => (
                <div key={key}>
                  <div className="text-lg font-semibold text-slate-900" dir="ltr">
                    {value}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t(`campaigns.totals.${key}` as 'campaigns.totals.spend')}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-500">{t('campaigns.roasNote')}</p>

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    {(
                      [
                        'name',
                        'spend',
                        'leads',
                        'cpl',
                        'qualified',
                        'cpql',
                        'sales',
                        'commission',
                        'roas',
                      ] as const
                    ).map((key) => (
                      <th key={key} scope="col" className="px-3 py-2 text-start">
                        {t(`campaigns.columns.${key}` as 'campaigns.columns.name')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {perf.map((row) => (
                    <tr key={row.campaign_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{row.campaign_name}</div>
                        <div className="text-xs text-slate-500">
                          {t(`platforms.${row.platform}` as 'platforms.meta')}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.spend)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">{row.leads}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.cpl)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">
                        {row.qualified}
                        {row.qualified_rate ? (
                          <span className="ms-1 text-xs text-slate-500">
                            ({row.qualified_rate}%)
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.cpql)}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">{row.sales}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.commission)}
                      </td>
                      <td className="px-3 py-2" dir="ltr">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            row.roas === null
                              ? 'bg-slate-100 text-slate-500'
                              : Number(row.roas) >= 1
                                ? 'bg-green-100 text-green-900'
                                : 'bg-red-100 text-red-900'
                          }`}
                        >
                          {row.roas === null ? '—' : `${row.roas}×`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {/* ---------------------------------------------------------- quality */}
      {tab === 'quality' ? (
        quality.isPending ? (
          <Spinner />
        ) : (
          <>
            <Alert tone="info">{t('campaigns.qualityIntro')}</Alert>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('campaigns.columns.name')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('campaigns.volumeRank')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('campaigns.qualityRank')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('campaigns.columns.cpql')}
                    </th>
                    <th scope="col" className="px-3 py-2 text-start">
                      {t('campaigns.verdict')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(quality.data ?? []).map((row) => {
                    // A positive gap means it ranks better on volume than on
                    // quality: it buys a lot of leads that are not worth much.
                    const gap = Number(row.rank_gap);
                    const Icon = gap > 0 ? ArrowDown : gap < 0 ? ArrowUp : Minus;
                    const tone =
                      gap > 0 ? 'text-red-700' : gap < 0 ? 'text-green-700' : 'text-slate-500';
                    const verdict = gap > 0 ? 'overrated' : gap < 0 ? 'underrated' : 'consistent';

                    return (
                      <tr key={row.campaign_id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {row.campaign_name}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-700">
                          #{row.volume_rank}
                          <span className="ms-1 text-xs text-slate-500">({row.leads})</span>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-700">
                          #{row.quality_rank}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                          {formatMoney(row.cpql)}
                        </td>
                        <td className={`px-3 py-2 ${tone}`}>
                          <span className="inline-flex items-center gap-1 text-xs font-medium">
                            <Icon className="size-3.5" aria-hidden="true" />
                            {t(`campaigns.verdicts.${verdict}` as 'campaigns.verdicts.consistent')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {/* ----------------------------------------------------------- manage */}
      {tab === 'manage' ? (
        campaigns.isPending ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('campaigns.columns.name')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('campaigns.platform')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('campaigns.period')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('campaigns.totalSpend')}
                  </th>
                  <th scope="col" className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(campaigns.data ?? []).map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {t(`platforms.${row.platform}` as 'platforms.meta')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                      {formatDate(row.start_date)} – {formatDate(row.end_date)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                      {formatMoney(row.total_spend)}
                    </td>
                    <td className="px-3 py-2 text-end">
                      {canEdit ? (
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            aria-label={t('campaigns.recordSpend')}
                            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                            onClick={() => setSpendFor(row)}
                          >
                            <Wallet className="size-4" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            aria-label={t('campaigns.edit')}
                            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
                            onClick={() => {
                              setEditing(row);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      <CampaignDialog open={dialogOpen} campaign={editing} onClose={() => setDialogOpen(false)} />
      <RecordSpendDialog
        open={Boolean(spendFor)}
        campaign={spendFor}
        onClose={() => setSpendFor(undefined)}
      />
    </section>
  );
}
